import * as THREE from 'three';
import { AppStore } from '../state/store';
import { SceneHost } from '../render/scene';
import { appendRctPiece, placeRctStart } from '../state/commands';
import {
  RCT_PIECES,
  RCT_GROUPS,
  RCT_PIECE_BY_ID,
  RctPieceId,
  rctEntryFrom,
  samplePiecePath,
  snapHeight,
} from '../core/track/rctPieces';
import { flashHint, setDefaultHint } from './toolbar';

/**
 * Rollercoaster Tycoon–style build tool: click a piece in the palette to
 * append it at the free end. The 3D view is orbit-only — no world-click build.
 */
export class RctMode {
  private active = false;
  private pieceId: RctPieceId = 'straight';
  private panel: HTMLElement;
  private buttons = new Map<RctPieceId, HTMLButtonElement>();
  private ghostLine: THREE.Line;
  private ghostGeo: THREE.BufferGeometry;
  private ghostMat: THREE.LineBasicMaterial;
  private tipMarker: THREE.Mesh;
  private savedHint = '';

  constructor(
    private store: AppStore,
    private host: SceneHost,
  ) {
    this.ghostMat = new THREE.LineBasicMaterial({
      color: 0x5ec8ff,
      transparent: true,
      opacity: 0.85,
      depthTest: false,
    });
    this.ghostGeo = new THREE.BufferGeometry();
    this.ghostLine = new THREE.Line(this.ghostGeo, this.ghostMat);
    this.ghostLine.renderOrder = 20;
    this.ghostLine.visible = false;
    host.scene.add(this.ghostLine);

    const tipGeo = new THREE.SphereGeometry(0.45, 12, 10);
    const tipMat = new THREE.MeshBasicMaterial({
      color: 0x5ec8ff,
      transparent: true,
      opacity: 0.9,
      depthTest: false,
    });
    this.tipMarker = new THREE.Mesh(tipGeo, tipMat);
    this.tipMarker.renderOrder = 21;
    this.tipMarker.visible = false;
    host.scene.add(this.tipMarker);

    this.panel = this.buildPanel();
    document.getElementById('viewport-wrap')!.appendChild(this.panel);

    window.addEventListener('keydown', (e) => this.onKey(e));

    store.on('track', () => {
      if (this.active) this.refreshGhost();
    });
    store.on('doc', () => {
      if (this.active) this.refreshGhost();
    });
    store.on('mode', () => {
      if (store.mode !== 'build' && this.active) this.setEnabled(false);
    });
  }

  get enabled(): boolean {
    return this.active;
  }

  get selectedPiece(): RctPieceId {
    return this.pieceId;
  }

  setEnabled(on: boolean): void {
    if (on === this.active) return;
    this.active = on;
    this.panel.classList.toggle('hidden', !on);
    this.ghostLine.visible = false;
    this.tipMarker.visible = false;
    if (on) {
      this.savedHint = document.getElementById('mode-hint')?.textContent ?? '';
      this.syncSelectionUi();
      this.refreshGhost();
      this.updateHint();
    } else if (this.savedHint) {
      setDefaultHint(this.savedHint);
    }
  }

  private buildPanel(): HTMLElement {
    const root = document.createElement('div');
    root.id = 'rct-palette';
    root.className = 'hidden';

    const title = document.createElement('div');
    title.className = 'rct-palette-title';
    title.textContent = 'Track pieces';
    root.appendChild(title);

    for (const group of RCT_GROUPS) {
      const heading = document.createElement('div');
      heading.className = 'rct-group-label';
      heading.textContent = group.label;
      root.appendChild(heading);

      const grid = document.createElement('div');
      grid.className = 'rct-piece-grid';
      for (const piece of RCT_PIECES.filter((p) => p.group === group.id)) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'rct-piece';
        b.title = `${piece.label} — ${piece.hint} (click to place)`;
        b.dataset.id = piece.id;
        const icon = document.createElement('span');
        icon.className = 'rct-piece-icon';
        icon.textContent = piece.short;
        const lab = document.createElement('span');
        lab.className = 'rct-piece-label';
        lab.textContent = piece.label;
        b.append(icon, lab);
        b.addEventListener('click', (e) => {
          e.stopPropagation();
          this.placePiece(piece.id);
        });
        grid.appendChild(b);
        this.buttons.set(piece.id, b);
      }
      root.appendChild(grid);
    }

    const actions = document.createElement('div');
    actions.className = 'rct-actions';
    const undoBtn = document.createElement('button');
    undoBtn.type = 'button';
    undoBtn.textContent = 'Undo';
    undoBtn.title = 'Undo last edit (Q / Ctrl+Z)';
    undoBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.store.undo();
      this.refreshGhost();
    });
    actions.append(undoBtn);
    root.appendChild(actions);

    return root;
  }

  private syncSelectionUi(): void {
    for (const [id, b] of this.buttons) {
      b.classList.toggle('active', id === this.pieceId);
    }
  }

  private updateHint(): void {
    const piece = RCT_PIECE_BY_ID[this.pieceId];
    if (this.store.doc.track.closed) {
      setDefaultHint('RCT build · open the track (Closed ✗) to append pieces');
    } else {
      setDefaultHint(
        `RCT build · click a piece to place · selected: ${piece.label} · Q undo`,
      );
    }
  }

  private entryAtTip(): { pos: THREE.Vector3; tan: THREE.Vector3; up: THREE.Vector3 } | null {
    const nodes = this.store.doc.track.nodes;
    if (nodes.length === 0 || this.store.doc.track.closed) return null;
    const L = this.store.runtime.totalLength;
    const end = this.store.runtime.frameAt(L);
    return rctEntryFrom(nodes[nodes.length - 1].p, end.tan, end.up);
  }

  refreshGhost(): void {
    if (!this.active) {
      this.ghostLine.visible = false;
      this.tipMarker.visible = false;
      return;
    }

    const entry = this.entryAtTip();
    if (!entry) {
      this.ghostLine.visible = false;
      this.tipMarker.visible = false;
      this.updateHint();
      return;
    }

    const piece = RCT_PIECE_BY_ID[this.pieceId];
    const result = piece.build(entry);
    const path = samplePiecePath(result, 0.6);
    this.ghostGeo.setFromPoints(path);
    this.ghostGeo.attributes.position.needsUpdate = true;
    this.ghostLine.visible = path.length > 1;

    this.tipMarker.position.copy(entry.pos);
    this.tipMarker.visible = true;
    this.updateHint();
  }

  /** Clicking a palette piece selects it and appends it at the tip. */
  private placePiece(id: RctPieceId): void {
    this.pieceId = id;
    this.syncSelectionUi();

    if (this.store.doc.track.closed) {
      flashHint('Open the track first (uncheck Closed) — pieces append at the free end');
      this.refreshGhost();
      return;
    }

    if (this.store.doc.track.nodes.length === 0) {
      // First piece: drop a station start on the grid, then append
      placeRctStart(this.store, new THREE.Vector3(0, snapHeight(3), 0), new THREE.Vector3(1, 0, 0));
    }

    const ok = appendRctPiece(this.store, id);
    if (!ok) {
      flashHint('Could not place piece');
      this.refreshGhost();
      return;
    }
    const nodes = this.store.doc.track.nodes;
    this.store.setSelection([nodes[nodes.length - 1].id]);
    this.refreshGhost();
  }

  private onKey(e: KeyboardEvent): void {
    if (!this.active || this.store.mode !== 'build') return;
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLSelectElement)
      return;

    if (e.key.toLowerCase() === 'q' && !e.ctrlKey && !e.metaKey) {
      e.preventDefault();
      this.store.undo();
      this.refreshGhost();
      return;
    }

    // number keys highlight first piece of each group (does not place)
    const groupHotkeys: Record<string, number> = { '1': 0, '2': 1, '3': 2, '4': 3 };
    const gi = groupHotkeys[e.key];
    if (gi !== undefined) {
      const group = RCT_GROUPS[gi];
      const first = RCT_PIECES.find((p) => p.group === group.id);
      if (first) {
        this.pieceId = first.id;
        this.syncSelectionUi();
        this.refreshGhost();
        this.updateHint();
      }
    }
  }
}
